import GameweekDetail from '../components/GameweekDetail'
import ModalBackdrop from '../components/ModalBackdrop'
import { adjacentIn, getAdjacentFinalizedGameweek } from '../season'
import { resolveActiveChips } from './gameweekResultChips'
import type { AppView } from './AppView'

/**
 * The gameweek results modal, wherever it is opened from. Not inside `AppModals`, which only
 * mounts in the tab shell: another player's profile is a pushed screen above that, so a
 * Best-gameweek tap there would have nothing to open into. Both render this.
 *
 * `view.history.viewingHistoryOwner` decides whose gameweek it is. Null is the signed-in user's,
 * read straight from their stored `seasonDetails`; set means another player's, assembled from a
 * lazy picks fetch.
 *
 * The arrows answer to whichever set opened the modal: from a multi-charge chip tile they step
 * that chip's uses, from another player's sharpest week their finalized season, otherwise they
 * walk your finalized season. Another player's gameweek with no walk keeps them off —
 * `getAdjacentFinalizedGameweek` walks *your* weeks, and arrows that quietly show your gameweeks
 * under their name are worse than no arrows.
 */
export default function GameweekResultsModal({ view }: { view: AppView }) {
  const { chips, seasonHistory, seasonDetails } = view
  const historyGameweek = view.history.viewingHistoryGameweek
  const owner = view.history.viewingHistoryOwner
  const walk = view.history.viewingHistoryWalk
  if (historyGameweek === null) return null

  const canStep = (direction: 'previous' | 'next') => {
    if (walk) return adjacentIn(walk, historyGameweek, direction) !== null
    if (owner) return false
    return getAdjacentFinalizedGameweek(seasonHistory, historyGameweek, direction) !== null
  }

  const viewingDetail = owner
    ? view.otherPlayerGameweekDetail
    : (seasonDetails[historyGameweek] ?? null)

  const activeChips = resolveActiveChips(owner !== null, viewingDetail, chips, historyGameweek)

  // Read off the week on screen, never off the tap that opened the modal: stepping away from
  // their sharpest week must drop the crown rather than carry it onto an ordinary week.
  const ownerIsBestGameweek = owner ? owner.bestGameweek === historyGameweek : undefined

  return (
    <ModalBackdrop
      onClose={view.history.transition.beginBack}
      // Results take over the screen — nothing behind them is worth reading through.
      scrim="immersive"
      // Entered through the shared-element expand: the clone owns the entrance, so the box
      // itself just fades.
      staticContentEntry={view.history.transition.detailEntry !== 'idle'}
    >
      {viewingDetail ? (
        <GameweekDetail
          detail={viewingDetail}
          entryTransition={
            owner
              ? undefined
              : {
                  state: view.history.transition.detailEntry,
                  anchorRef: view.history.transition.registerDestAnchor,
                }
          }
          ownerName={owner?.displayName}
          ownerIsBestGameweek={ownerIsBestGameweek}
          onBack={view.history.transition.beginBack}
          onPrevious={() => view.history.step('previous')}
          onNext={() => view.history.step('next')}
          canGoPrevious={canStep('previous')}
          canGoNext={canStep('next')}
          activeChips={activeChips}
        />
      ) : (
        <div className="app__empty-detail">
          {/* The arrows live here too: a step lands before its picks arrive, and a fallback with
              no way out would be the dead end this screen just stopped being. */}
          <div className="app__empty-detail-nav">
            <button
              className="gameweek-detail__nav-btn"
              onClick={() => view.history.step('previous')}
              disabled={!canStep('previous')}
              aria-label="Previous gameweek"
            >
              ‹
            </button>
            <p className="app__empty-detail-text">
              No detail available for Gameweek {historyGameweek} yet.
            </p>
            <button
              className="gameweek-detail__nav-btn"
              onClick={() => view.history.step('next')}
              disabled={!canStep('next')}
              aria-label="Next gameweek"
            >
              ›
            </button>
          </div>
          <button
            className="app__empty-detail-close"
            onClick={view.history.transition.beginBack}
          >
            Close
          </button>
        </div>
      )}
    </ModalBackdrop>
  )
}
